import React from 'react';
import { Table, Input, Button, Dropdown, Menu, Checkbox, Steps, Space, Flex, Tag, TableColumnProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import ReusableTable from '../../../../components/TableComponent';
import { MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import ProfileCard from '../../../../components/ContactInfo/ProfileCard';
import moment from 'moment';
import StatusBadge from '../../../Leaves/AttendanceComponents/history/StatusBadge';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { align } from 'suneditor/src/plugins';

const { Search } = Input;

const columns = [
    {
        title: '',
        dataIndex: 'checkbox',
        render: () => <Checkbox />,
        width: 50,
    },
    {
        title: 'EMPLOYEE',
        dataIndex: 'employee',
    },
    {
        title: 'DATE',
        dataIndex: 'date',
    },
    {
        title: 'TOTAL DAYS',
        dataIndex: 'totalDays',
    },
    {
        title: 'LEAVE TYPE',
        dataIndex: 'leaveType',
    },
    {
        title: 'STATUS',
        dataIndex: 'status',
    },
    {
        title: 'APPROVER',
        dataIndex: 'approver',
    },
];

const Reimbursement = () => {
    const [currentStep, setCurrentStep] = React.useState(0);


    const leaveConfig: MasterConfigs = {
        api: () => '/run-payroll/leaves',
        queryParams: {
            month: '06-2025'
        },
        tableProps: {
            rowKey: '_id',
            rowSelection: {
                type: 'checkbox',
                onChange: (selectedRowKeys, selectedRows) => {
                    console.log('Selected Row Keys:', selectedRowKeys);
                    console.log('Selected Rows:', selectedRows);
                }
            },
        },
        getColumns: () => [
            {
                title: 'Employee',
                dataIndex: 'user',
                ellipsis: true,
                width: 150,
                render: (text, records) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                    subTitle={text.workEmail}
                />
            },
            {
                title: 'From Date',
                dataIndex: 'startDate',
                align: 'center',
                render: (text) => moment(text).format('DD MMM YYYY')
            },
            {
                title: 'Total Days',
                dataIndex: 'days',
                align: 'center',
                render: (text) => text + ' Days'
            },
            {
                title: 'Effective Leave Days in Month',
                dataIndex: 'effectiveLeaveDaysInMonth',
                render: (text) => text + ' Days',
                align: 'center',
            },
            {
                title: 'Leave Type',
                dataIndex: 'leaves',
                render: (text) => text.leaveType?.leaveName || 'N/A'
            },
            {
                title: 'Leave Status',
                align: 'center',
                dataIndex: 'leaveStatus',
                render: (text) => {
                    const statusMap = {
                        'approved': { color: 'green', text: 'Approved' },
                        'pending': { color: 'orange', text: 'Pending' }
                    };
                    const status = statusMap[text] || { color: 'blue', text: 'Unknown' };
                    return <Tag color={status?.color} bordered={false}>{status.text}</Tag>;
                }
            }
        ]
    }

    const config: MasterConfigs = {
        api: () => '/run-payroll/leave-and-no-attendance',
        queryParams: {
            month: '06-2025'
        },
        tableProps: {
            rowKey: '_id',
            // rowSelection: {
            //     type: 'checkbox',
            //     onChange: (selectedRowKeys, selectedRows) => {
            //         console.log('Selected Row Keys:', selectedRowKeys);
            //         console.log('Selected Rows:', selectedRows);
            //     }
            // },
            scroll: { y: 450 },
            expandable: {
                expandedRowRender: (record) => {
                    return <Table
                        className='w-full mt-2'
                        columns={getAttendanceColumns()}
                        dataSource={record.attendanceLogs.map((log) => ({
                            key: log._id,
                            ...log,
                        }))}
                        scroll={{ y: 300 }}
                        pagination={{
                            pageSize: 7
                        }}
                        rowClassName={(record) =>
                            record.dayStatus === "absent" || record.totalHours < 1 && record.dayStatus == 'present'
                                ? "bg-red-50"
                                : ""
                        }
                    />
                },
                rowExpandable: (record) => true,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }

    const getAttendanceColumns = (): TableColumnProps[] => [
        {
            title: "Date",
            dataIndex: "date",
            width: 200,
            fixed: 'left',
            render: (date) => moment(date).format("dddd DD MMM, YYYY"),
        },
        {
            title: 'Clock In',
            dataIndex: 'startDate',
            key: 'startDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchStatus == 'on-time') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.startDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + (record?.punchStatus == 'on-time' ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchStatus}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: 'Clock Out',
            dataIndex: 'endDate',
            key: 'endDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchOut == 'on-time' || record.punchOut == 'late') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.endDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + ((record?.punchOut == 'on-time' || record?.punchOut == 'late') ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchOut}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: "Working Hours",
            dataIndex: "totalHours",
            width: 150,
            align: 'center',
            render: (hours, record) => record?.dayStatus == 'present' ? <>
                {
                    hours <= 5 ? (
                        <span className='text-red-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) : hours > 5 && hours <= 7 ? (
                        <span className='text-red-800 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) :
                        <span className='text-green-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                }
            </> : '-',
        },
        {
            title: "Day Status",
            dataIndex: "dayStatus",
            width: 150,
            align: 'center',
            render: (status) => {
                const colorMap: Record<string, string> = {
                    present: "green",
                    absent: "red",
                    weekend: "blue",
                    leave: "orange",
                };
                return <Tag bordered={false} color={colorMap[status] || "default"}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: "Leave Type",
            width: 150,
            render: (_, record) =>
                record.leaveData?.leaveDetail?.leaveTypeDetail?.leaveName || "-",
        },
        {
            title: "Leave Status",
            width: 150,
            align: 'center',
            render: (_, record) =>
                record.leaveData?.leaveStatus ? (
                    <Tag color="green">{record.leaveData.leaveStatus}</Tag>
                ) : (
                    "-"
                ),
        },
    ];

    const employeeColumns = [
        {
            title: "Employee",
            align: 'left',
            render: (_, record) => <SmallProfileCard
                name={constants.GET_FULL_NAME(record)}
                avatar={record?.avatar}
                subTitle={record?.employeeId}
            />
        },
        {
            title: "Shift",
            align: 'center',
            dataIndex: "shiftName",
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "workingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "totalLeaves",
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
    ];

    const configs = {
        0: leaveConfig,
        1: config,
        2: config,
        3: config,
    }


    return (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
            <div style={{ flex: '0 0 auto' }} className='bg-white shadow-md p-4  w-full'>
                <Flex align='center' gap={16}>
                    <div className='w-3/4 flex items-center gap-4'>
                        <Steps
                            current={currentStep}
                            size='small'
                            onChange={(e) => setCurrentStep(e)}
                            items={[
                                { title: 'Salary Component Claim' },
                                { title: 'Expenses' },
                                { title: 'Adhoc Payments' },
                                { title: 'Adhoc Deductions' }
                            ]}
                        />
                    </div>
                    <div className='w-1/4 flex justify-end'>
                        <Button type="primary">Save & Continue</Button>
                    </div>
                </Flex>
            </div>

            <div
                style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb' // same as bg-gray-50
                }}
            >
                <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                    All leave (approved or pending) that falls under this payroll cycle month will be displayed here.
                </div>
                {currentStep >= 0 && <ReusableTable config={configs[currentStep]} key={currentStep} />}
            </div>
        </div>

    );
};

export default Reimbursement;
